import React from "react";
import { Fade } from "react-awesome-reveal";
import {
  FaMapLocationDot,
  FaCompass,
  FaArrowUpRightFromSquare,
  FaPhone,
  FaEnvelope,
  FaClock,
} from "react-icons/fa6";

const GoogleMapEmbed = ({ latitude, longitude, placeName }) => {
  // Updated with exact coordinates matching Kunjo Chaya, Bayezid, Chattogram
  const lat = latitude || "22.3845";
  const lng = longitude || "91.8153";
  const name = placeName || "Our Location";

  // Retains the exact embed structural format from your original code
  const googleMapsUrl = `https://maps.google.com/maps?q=${lat},${lng}&z=16&output=embed`;

  // Directly maps to your client's custom sharing link for navigation
  const externalMapsUrl = "https://maps.app.goo.gl/HjKfHZDqD1ZGYbjh8?g_st=aw";

  return (
    <div className="bg-gradient-to-br from-stone-50 via-white to-green-50/20 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto w-full">
        {/* Header Segment */}
        <div className="text-center mb-12">
          <Fade direction="up" triggerOnce>
            <span className="text-sm font-semibold tracking-widest uppercase text-emerald-800 block mb-2">
              Find Your Way
            </span>
            <h2 className="text-4xl font-extrabold text-green-950 tracking-tight">
              {name}
            </h2>
            <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
              Join us for daily congregational prayers and community events.
              Find our geographic coordinates below.
            </p>
          </Fade>
        </div>

        {/* Dashboard Grid Container */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch mt-8">
          {/* Left Block: Location Navigation Quick Links */}
          <div className="lg:col-span-4 flex flex-col justify-between bg-white border border-stone-100 p-6 rounded-2xl shadow-sm transform transition-all duration-500 hover:shadow-xl hover:-translate-y-1">
            <div className="space-y-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-800 flex items-center justify-center shadow-inner">
                <FaMapLocationDot className="w-5 h-5" />
              </div>

              <div>
                <h3 className="text-lg font-bold text-gray-900 tracking-tight">
                  Physical Address
                </h3>
                <p className="text-emerald-950 font-bold text-sm mt-1 leading-relaxed">
                  Road 1, Kunjo Chaya, Bayezid, Chattogram
                </p>
              </div>

              {/* Contact Information Details */}
              <div className="bg-stone-50 border border-stone-100 rounded-xl p-4 space-y-3 text-xs text-gray-600">
                <div className="flex items-start gap-2.5">
                  <FaPhone className="w-3.5 h-3.5 text-emerald-800 mt-0.5 shrink-0" />
                  <div>
                    <span className="block font-bold text-gray-900">
                      Phone & Mobile
                    </span>
                    <a
                      href="tel:+8801700000000"
                      className="hover:text-emerald-800 transition-colors"
                    >
                      +880 17XX-XXXXXX
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 border-t border-stone-200/60 pt-2">
                  <FaEnvelope className="w-3.5 h-3.5 text-emerald-800 mt-0.5 shrink-0" />
                  <div>
                    <span className="block font-bold text-gray-900">
                      Email Address
                    </span>
                    <a
                      href="mailto:mohammodiabaria@gmail.com"
                      className="hover:text-emerald-800 transition-colors"
                    >
                      mohammodiabaria@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 border-t border-stone-200/60 pt-2">
                  <FaClock className="w-3.5 h-3.5 text-emerald-800 mt-0.5 shrink-0" />
                  <div>
                    <span className="block font-bold text-gray-900">
                      Timing
                    </span>
                    <p className="text-stone-500">
                      Open daily from Fajr to Isha
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Native Link Dynamic Trigger Action button */}
            <div className="mt-8 lg:mt-0">
              <a
                href={externalMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-green-950 text-white font-medium rounded-xl hover:bg-green-800 transition-all duration-300 transform hover:-translate-y-0.5 shadow-md hover:shadow-lg text-sm"
              >
                <FaCompass />
                <span>Get Driving Directions</span>
                <FaArrowUpRightFromSquare className="w-3 h-3 ml-1 opacity-70" />
              </a>
            </div>
          </div>

          {/* Right Block: Map Embedded Box Frame */}
          <div className="lg:col-span-8 w-full">
            <div className="relative overflow-hidden rounded-2xl bg-stone-100 border border-stone-100 shadow-sm h-[350px] sm:h-[400px] md:h-[450px] lg:h-full min-h-[400px] transform transition-all duration-500 hover:shadow-xl hover:-translate-y-1 group">
              {/* Top border ambient indicator line element */}
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-emerald-700 to-green-900 rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20" />

              <iframe
                src={googleMapsUrl}
                className="w-full h-full relative z-10 filter grayscale-[15%] contrast-[105%] hover:grayscale-0 transition-all duration-700"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`${name} Map Embed Widget Viewport`}
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleMapEmbed;
